import React, { useState, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react'
import { computeROI, DEFAULT_ROI_INPUTS } from './roiCalcEngine'
import { openPrintView, fmt as pf, cardsHTML, pillsHTML, plRowsHTML, tableHTML, inputsGridHTML, sectionHTML, longTermChartSVG, cocSensitivityChartSVG } from './printCalculator'

// ── Formatters ────────────────────────────────────────────────────────────────
const currency = (v, d = 0) => {
  if (v === null || v === undefined || !isFinite(v)) return '—'
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
  return v < 0 ? `-$${s}` : `$${s}`
}
const pct = (v, d = 2) => isFinite(v) ? `${(v * 100).toFixed(d)}%` : '—'
const negCurrency = (v, d = 0) => v ? `-${currency(v, d)}` : currency(0, d)
const num = (v, d = 2) => isFinite(v) ? v.toFixed(d) : '—'

// ── Form Primitives ──────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="calc-field__label">{label}</label>
      {children}
      {hint && <p className="calc-field__hint">{hint}</p>}
    </div>
  )
}

function Inp({ value, onChange, pre, suf, step = 'any', min = 0 }) {
  const [display, setDisplay] = useState(String(value))
  const [focused, setFocused] = useState(false)
  // Sync display when value changes externally (not during editing)
  React.useEffect(() => { if (!focused) setDisplay(String(value)) }, [value, focused])
  const inputCls = `calc-input${pre ? ' calc-input--has-pre' : ''}${suf ? ' calc-input--has-suf' : ''}`
  return (
    <div className="calc-input-wrap">
      {pre && <span className="calc-input-wrap__affix calc-input-wrap__affix--pre">{pre}</span>}
      <input
        type="number"
        step={step}
        min={min}
        value={focused ? display : value}
        onFocus={() => { setFocused(true); setDisplay(String(value)) }}
        onChange={(e) => { setDisplay(e.target.value); const n = parseFloat(e.target.value); if (!isNaN(n)) onChange(n) }}
        onBlur={() => { setFocused(false); if (display === '' || isNaN(parseFloat(display))) onChange(0) }}
        className={inputCls}
      />
      {suf && <span className="calc-input-wrap__affix calc-input-wrap__affix--suf">{suf}</span>}
    </div>
  )
}

function Group({ title, children }) {
  return (
    <div className="calc-group">
      <div className="calc-group__header">
        <div className="calc-group__title">{title}</div>
      </div>
      <div className="calc-group__body">{children}</div>
    </div>
  )
}

// ── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, rose }) {
  const cls = `calc-stat${rose ? ' calc-stat--rose' : ''}`
  return (
    <div className={cls}>
      <div className="calc-stat__label">{label}</div>
      <div className="calc-stat__value">{value}</div>
      {sub && <div className="calc-stat__sub">{sub}</div>}
    </div>
  )
}

// ── Section Banner ───────────────────────────────────────────────────────────
function SectionBanner({ title }) {
  return (
    <div className="calc-banner">
      <div className="calc-banner__title">{title}</div>
    </div>
  )
}

// ── PL Row ───────────────────────────────────────────────────────────────────
function PLRow({ label, value, indent, bold, red, green }) {
  const labelCls = `calc-pl-row__label${bold ? ' calc-pl-row__label--bold' : ''}${indent ? ' calc-pl-row__label--indent' : ''}`
  const valueCls = `calc-pl-row__value${bold ? ' calc-pl-row__value--bold' : ''}${red ? ' calc-pl-row__value--red' : ''}${green ? ' calc-pl-row__value--green' : ''}`
  return (
    <div className="calc-pl-row">
      <span className={labelCls}>{label}</span>
      <span className={valueCls}>{value}</span>
    </div>
  )
}

// ── 3-Year Pro Forma Table ───────────────────────────────────────────────────
function ProFormaTable({ proForma }) {
  const rows = [
    { label: 'Gross Rental Income',   key: 'grossRent',        fmt: (v) => currency(v) },
    { label: 'Vacancy Loss',          key: 'vacancyLoss',      fmt: (v) => negCurrency(v) },
    { label: 'Net Rental Income',     key: 'netRental',        fmt: (v) => currency(v) },
    { label: 'Other Income',          key: 'otherIncome',      fmt: (v) => currency(v) },
    { label: 'Gross Income',          key: 'grossIncome',      fmt: (v) => currency(v), bold: true, green: true },
    { label: '─', key: null },
    { label: 'Property Taxes',        key: 'expTaxes',         fmt: (v) => negCurrency(v) },
    { label: 'Insurance',             key: 'expInsurance',     fmt: (v) => negCurrency(v) },
    { label: 'Management',            key: 'expMgmt',          fmt: (v) => negCurrency(v) },
    { label: 'Maintenance',           key: 'expMaint',         fmt: (v) => negCurrency(v) },
    { label: 'Utilities',             key: 'expUtilities',     fmt: (v) => negCurrency(v) },
    { label: 'HOA',                   key: 'expHoa',           fmt: (v) => negCurrency(v) },
    { label: 'Misc',                  key: 'expMisc',          fmt: (v) => negCurrency(v) },
    { label: 'Total Expenses',        key: 'totalExpenses',    fmt: (v) => negCurrency(v), bold: true, red: true },
    { label: '─', key: null },
    { label: 'NOI',                   key: 'noi',              fmt: (v) => currency(v),  bold: true, hi: true },
    { label: 'Mortgage (annual)',     key: 'annualCashFlow',   fmt: (_, row) => currency(row.noi - row.annualCashFlow < 0 ? row.noi - row.annualCashFlow : -(row.noi - row.annualCashFlow)), red: true },
    { label: 'Net Cash Flow',         key: 'annualCashFlow',   fmt: (v) => currency(v),  bold: true, hi: true },
    { label: 'Cash-on-Cash ROI',      key: 'cashROI',          fmt: (v) => pct(v), autoRose: true },
    { label: '─', key: null },
    { label: 'Remaining Balance',     key: 'remainingBalance', fmt: (v) => currency(v) },
    { label: 'Equity Accrued',        key: 'equityAccrued',    fmt: (v) => currency(v) },
    { label: 'Property Value',        key: 'propertyValue',    fmt: (v) => currency(v) },
    { label: 'Appreciation',          key: 'appreciation',     fmt: (v) => currency(v) },
    { label: 'Total Return',          key: 'totalReturn',      fmt: (v) => currency(v),  bold: true, autoRose: true },
    { label: 'Total ROI',             key: 'totalROI',         fmt: (v) => pct(v),  bold: true, hi: true },
  ]

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="calc-table calc-table--min-480">
        <thead>
          <tr>
            <th>Line Item</th>
            {proForma.map((yr) => (
              <th key={yr.year}>Year {yr.year}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label, key, fmt, bold, red, hi, green, autoRose }, i) => {
            if (label === '─') {
              return (
                <tr key={i} className="calc-table__divider">
                  <td colSpan={proForma.length + 1}><div style={{ borderTop: '1px solid var(--clr-border)' }} /></td>
                </tr>
              )
            }
            const valColor = (yr) => {
              const raw = yr[key]
              if (hi && typeof raw === 'number' && raw < 0) return 'var(--clr-rose)'
              if (hi) return 'var(--clr-green)'
              if (green) return 'var(--clr-green)'
              if (red) return 'var(--clr-rose)'
              if (autoRose && typeof raw === 'number' && raw < 0) return 'var(--clr-rose)'
              return 'var(--clr-charcoal)'
            }
            return (
              <tr key={i} className={hi ? 'calc-table__highlight' : undefined}>
                <td style={{ fontWeight: bold ? 600 : 400, color: bold ? 'var(--clr-charcoal)' : 'var(--clr-grey)' }}>{label}</td>
                {proForma.map((yr) => (
                  <td key={yr.year} style={{ fontWeight: bold ? 600 : 400, color: valColor(yr) }}>
                    {fmt ? fmt(yr[key], yr) : '—'}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── 30-Year SVG Chart ────────────────────────────────────────────────────────
function LongTermChart({ longTerm }) {
  const W = 700, H = 240
  const PAD = { t: 44, r: 20, b: 48, l: 80 }
  const iW = W - PAD.l - PAD.r
  const iH = H - PAD.t - PAD.b
  const displayed = longTerm.filter((_, i) => i < 30)
  const barW = iW / displayed.length
  const maxVal = Math.max(...displayed.map((d) => Math.max(0, d.annualCashFlow) + Math.max(0, d.equityAccrued) + Math.max(0, d.appreciation)), 1)
  const minVal = Math.min(...displayed.map((d) => Math.min(0, d.annualCashFlow)), 0)
  const totalRange = maxVal - minVal
  const zeroY = PAD.t + (maxVal / totalRange) * iH
  const roiMax = Math.max(...displayed.map((d) => d.totalROI), 0.01)
  const roiLine = displayed.map((d, i) => {
    const x = PAD.l + (i + 0.5) * barW
    const y = zeroY - (Math.max(0, d.totalROI) / roiMax) * (zeroY - PAD.t)
    return `${x},${y}`
  }).join(' ')

  return (
    <div style={{ paddingBottom: 5.5 }}>
      <div className="calc-legend">
        {[
          { color: '#F16722', label: 'Cash Flow (+)' },
          { color: '#CA1551', label: 'Cash Flow (-)' },
          { color: '#3D9AA7', label: 'Equity' },
          { color: '#94C9D1', label: 'Appreciation' },
        ].map(({ color, label }) => (
          <div key={label} className="calc-legend__item">
            <span className="calc-legend__swatch" style={{ backgroundColor: color }} />
            {label}
          </div>
        ))}
        <div className="calc-legend__item calc-legend__item--right" style={{ fontWeight: 700, color: '#0E6673' }}>
          <svg width="28" height="4"><line x1="0" y1="2" x2="28" y2="2" stroke="#0E6673" strokeWidth="3" /></svg>
          Annual Total ROI %
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 240 }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = PAD.t + f * iH
          const val = maxVal - f * totalRange
          return (
            <g key={f}>
              <line x1={PAD.l} x2={PAD.l + iW} y1={y} y2={y} stroke="#EFF2F6" strokeWidth="1" />
              <text x={PAD.l - 6} y={y + 5} textAnchor="end" fontSize="14" fontWeight="600" fill="#66707D">{currency(val, 0)}</text>
            </g>
          )
        })}
        {minVal < 0 && (
          <line x1={PAD.l} x2={PAD.l + iW} y1={zeroY} y2={zeroY} stroke="#99A0AA" strokeWidth="2" />
        )}
        {displayed.map((d, i) => {
          const x = PAD.l + i * barW + barW * 0.12
          const bw = barW * 0.76
          const showLabel = displayed.length <= 15 || (i + 1) % 5 === 0 || i === 0
          const cf = d.annualCashFlow
          const cfPosH = (Math.max(0, cf) / totalRange) * iH
          const cfNegH = (Math.max(0, -cf) / totalRange) * iH
          const h2 = (Math.max(0, d.equityAccrued) / totalRange) * iH
          const h3 = (Math.max(0, d.appreciation) / totalRange) * iH
          const stackTop = zeroY - cfPosH - h2 - h3
          const stackBot = zeroY + cfNegH
          const stackH = stackBot - stackTop
          const clipId = `bar-clip-${d.year}`
          return (
            <g key={d.year}>
              <defs>
                <clipPath id={clipId}>
                  <rect x={x} y={stackTop} width={bw} height={stackH} rx="2.5" />
                </clipPath>
              </defs>
              <g clipPath={`url(#${clipId})`}>
                {cfPosH > 0 && <rect x={x} y={zeroY - cfPosH} width={bw} height={cfPosH} fill="#F16722" />}
                {cfNegH > 0 && <rect x={x} y={zeroY} width={bw} height={cfNegH} fill="#CA1551" />}
                <rect x={x} y={zeroY - cfPosH - h2} width={bw} height={h2} fill="#3D9AA7" />
                <rect x={x} y={zeroY - cfPosH - h2 - h3} width={bw} height={h3} fill="#94C9D1" />
              </g>
              {showLabel && <text x={x + bw / 2} y={H - PAD.b + 16} textAnchor="middle" fontSize="14" fontWeight="600" fill="#66707D">Y{d.year}</text>}
            </g>
          )
        })}
        <polyline points={roiLine} fill="none" stroke="#0E6673" strokeWidth="3" strokeLinejoin="round" />
        {displayed.filter((_, i) => i % 5 === 0 || i === displayed.length - 1).map((d) => {
          const idx = displayed.indexOf(d)
          const x = PAD.l + (idx + 0.5) * barW
          const y = zeroY - (Math.max(0, d.totalROI) / roiMax) * (zeroY - PAD.t)
          const pillW = 48, pillH = 21, pillR = 6
          const pillX = x - pillW / 2, pillY = y - pillH - 12
          return (
            <g key={d.year}>
              <circle cx={x} cy={y} r="5" fill="#0E6673" stroke="white" strokeWidth="2" />
              <rect x={pillX} y={pillY} width={pillW} height={pillH} rx={pillR} fill="#0E6673" />
              <polygon points={`${x - 4},${pillY + pillH} ${x + 4},${pillY + pillH} ${x},${pillY + pillH + 5}`} fill="#0E6673" />
              <text x={x} y={pillY + 14.5} textAnchor="middle" fontSize="12" fontWeight="700" fill="white">{pct(d.totalROI, 2)}</text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// ── COC Sensitivity Chart ────────────────────────────────────────────────────
function CocSensitivityChart({ data, currentPct }) {
  const W = 560, H = 180
  const PAD = { t: 16, r: 20, b: 40, l: 56 }
  const iW = W - PAD.l - PAD.r
  const iH = H - PAD.t - PAD.b
  const minCoc = Math.min(...data.map((d) => d.cocROI))
  const maxCoc = Math.max(...data.map((d) => d.cocROI))
  const range = maxCoc - minCoc || 0.01
  const pts = data.map((d, i) => {
    const x = PAD.l + (i / (data.length - 1)) * iW
    const y = PAD.t + iH - ((d.cocROI - minCoc) / range) * iH
    return `${x},${y}`
  }).join(' ')
  const curIdx = data.findIndex((d) => d.pct === Math.round(currentPct * 100))
  const curD = curIdx >= 0 ? data[curIdx] : null
  const curX = curD ? PAD.l + (curIdx / (data.length - 1)) * iW : null
  const curY = curD ? PAD.t + iH - ((curD.cocROI - minCoc) / range) * iH : null

  return (
    <div>
      <p className="calc-chart-desc">How cash-on-cash ROI changes with down payment % — all other inputs constant</p>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 200 }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = PAD.t + iH * f
          const v = maxCoc - f * range
          return (
            <g key={f}>
              <line x1={PAD.l} x2={PAD.l + iW} y1={y} y2={y} stroke="#EFF2F6" strokeWidth="1" />
              <text x={PAD.l - 6} y={y + 4} textAnchor="end" fontSize="9" fill="#66707D">{pct(v, 2)}</text>
            </g>
          )
        })}
        {data.map((d, i) => {
          const x = PAD.l + (i / (data.length - 1)) * iW
          return <text key={d.pct} x={x} y={H - PAD.b + 13} textAnchor="middle" fontSize="9" fill="#66707D">{d.pct % 5 === 0 ? `${d.pct}%` : ''}</text>
        })}
        <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="9" fill="#66707D">Down Payment %</text>
        <polyline points={pts} fill="none" stroke="#128292" strokeWidth="2" strokeLinejoin="round" />
        {curX && curY && (
          <>
            <line x1={curX} y1={PAD.t} x2={curX} y2={PAD.t + iH} stroke="#F16722" strokeWidth="1.5" strokeDasharray="4 2" />
            <circle cx={curX} cy={curY} r="5" fill="#F16722" stroke="white" strokeWidth="1.5" />
            <rect x={curX - 28} y={curY - 22} width="56" height="16" rx="4" fill="#F16722" />
            <text x={curX} y={curY - 11} textAnchor="middle" fontSize="9" fontWeight="bold" fill="white">{pct(curD.cocROI, 2)}</text>
          </>
        )}
      </svg>
    </div>
  )
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = ['Overview', 'Statement', 'Pro Forma', 'Projections', 'CoC vs Down Payment']

// ── Main Component ──────────────────────────────────────────────────────────
const RentalROICalculator = forwardRef(function RentalROICalculator({ initialListingPrice, initialYearBuilt, initialRentMonthly, initialPropertyTax, address = ''  }, ref) {
  const [inp, setInp] = useState(() => ({
    ...DEFAULT_ROI_INPUTS,
    ...(initialListingPrice != null && { listingPrice: initialListingPrice }),
    ...(initialYearBuilt    != null && { yearBuilt:    initialYearBuilt }),
    ...(initialRentMonthly  != null && { rentMonthly:  initialRentMonthly }),
    ...(initialPropertyTax  != null && { propertyTaxFlat: Math.round(initialPropertyTax) }),
  }))
  const [tab, setTab] = useState('Overview')
  const [propTaxMode, setPropTaxMode] = useState(initialPropertyTax != null ? 'flat' : 'pct')
  const set    = (k) => (v) => setInp((prev) => ({ ...prev, [k]: v }))
  const setPct = (k) => (v) => setInp((prev) => ({ ...prev, [k]: v / 100 }))

  const r = useMemo(() => computeROI({ ...inp, propTaxMode }), [inp, propTaxMode])

  useImperativeHandle(ref, () => ({
    getInputs:  () => ({ ...inp }),
    getResults: () => ({ ...r }),
    setInputs:  (updates) => setInp((prev) => ({ ...prev, ...updates })),
  }), [inp, r])

  const handlePrint = useCallback(() => {
    const c = pf.currency, p = pf.pct, n = pf.num
    const kpis = [
      { label: 'Annual Cash Flow', value: c(r.annualCashFlow), color: r.annualCashFlow >= 0 ? '#5AC471' : '#CA1551' },
      { label: 'Cap Rate',         value: p(r.capRate),         color: r.capRate >= 0 ? '#5AC471' : '#CA1551' },
      { label: 'CoC ROI',          value: p(r.cocROI),          color: r.cocROI >= 0 ? '#5AC471' : '#CA1551' },
      { label: 'Total ROI Yr1',    value: p(r.totalROI_yr1),    color: r.totalROI_yr1 >= 0 ? '#5AC471' : '#CA1551' },
      { label: 'Net Op. Income',   value: c(r.noi),             color: r.noi >= 0 ? '#5AC471' : '#CA1551' },
    ]

    const inputsHTML = inputsGridHTML([
      { title: 'Property', items: [
        { label: 'Purchase Price', value: c(inp.listingPrice) },
        { label: 'Improvements', value: c(inp.improvements) },
        { label: 'Year Built', value: inp.yearBuilt },
        { label: '# of Units', value: inp.unitCount },
      ]},
      { title: 'Financing', items: [
        { label: 'Down Payment', value: `${(inp.downPaymentPct * 100).toFixed(1)}%` },
        { label: 'Interest Rate', value: `${(inp.interestRate * 100).toFixed(3)}%` },
        { label: 'Mortgage Term', value: `${inp.mortgageYears} years` },
        { label: 'Loan Amount', value: c(r.loanAmount) },
        { label: 'Monthly Payment', value: c(r.monthlyPayment) },
      ]},
      { title: 'Revenue', items: [
        { label: 'Monthly Rent', value: c(inp.rentMonthly) },
        { label: 'Other Revenue/mo', value: c(inp.otherRevMonthly) },
        { label: 'Vacancy Rate', value: `${(inp.vacancyRate * 100).toFixed(1)}%` },
      ]},
      { title: 'Operating Expenses', items: [
        { label: 'Property Tax/yr', value: propTaxMode === 'pct' ? `${(inp.propertyTaxPct * 100).toFixed(2)}% (${c(r.expTaxes)})` : c(inp.propertyTaxFlat) },
        { label: 'Insurance/yr', value: c(inp.insuranceYearly) },
        { label: 'Management Fee', value: `${(inp.managementFeePct * 100).toFixed(1)}%` },
        { label: 'Maintenance Reserve', value: `${(inp.maintenanceResPct * 100).toFixed(1)}%` },
        { label: 'Utilities/mo', value: c(inp.utilitiesMonthly) },
        { label: 'HOA/yr', value: c(inp.hoaYearly) },
        { label: 'Advertising/yr', value: c(inp.advertisingYearly) },
        { label: 'Misc/yr', value: c(inp.miscYearly) },
      ]},
      { title: 'Growth Assumptions', items: [
        { label: 'Revenue Growth', value: `${(inp.revenueGrowthPct * 100).toFixed(2)}%` },
        { label: 'Expense Growth', value: `${(inp.expenseGrowthPct * 100).toFixed(2)}%` },
        { label: 'Appreciation', value: `${(inp.appreciationPct * 100).toFixed(2)}%` },
      ]},
    ])

    const pages = [
      { title: 'Overview', html:
        sectionHTML('Key Performance Indicators', cardsHTML([
          { label: 'Annual Cash Flow',  value: c(r.annualCashFlow),  sub: `${c(r.monthlyCashFlow)}/mo`, color: r.annualCashFlow < 0 ? '#CA1551' : undefined },
          { label: 'Cap Rate',           value: p(r.capRate),         sub: 'NOI ÷ price' },
          { label: 'Cash-on-Cash ROI',   value: p(r.cocROI),          sub: 'Year 1', color: r.cocROI < 0 ? '#CA1551' : undefined },
          { label: 'Total ROI Year 1',   value: p(r.totalROI_yr1),    sub: 'Cash+Equity+Apprec.', color: r.totalROI_yr1 < 0 ? '#CA1551' : undefined },
          { label: 'NOI',                value: c(r.noi),             sub: 'Net operating income' },
          { label: 'GRM',                value: n(r.grm, 1),         sub: 'Price ÷ annual rent' },
          { label: 'Cost / Unit',        value: c(r.costPerUnit),     sub: `${inp.unitCount} unit${inp.unitCount !== 1 ? 's' : ''}` },
          { label: 'Monthly CF / Unit',  value: c(r.monthlyPerUnit),  sub: 'Per unit / month' },
        ])) +
        sectionHTML('Acquisition Cost Stack', pillsHTML([
          { label: 'Sales Price',    value: c(r.salesPrice) },
          { label: '+ Closing 2%',  value: c(r.closingCosts) },
          { label: '+ Improvements',value: c(r.improvements) },
          { label: '= Total Cost',   value: c(r.totalCost),  variant: 'teal' },
          { label: 'Down Payment',   value: c(r.downPayment), variant: 'orange' },
          { label: 'Cash Outlay',    value: c(r.cashOutlay),  variant: 'orange' },
        ])) +
        sectionHTML('Year 1 Return Breakdown', pillsHTML([
          { label: 'Cash Flow',    value: c(r.annualCashFlow) },
          { label: 'Equity',       value: c(r.equity_yr1) },
          { label: 'Appreciation', value: c(r.appreciation_yr1) },
          { label: 'Total Return', value: c(r.totalReturn_yr1),  variant: 'rose' },
          { label: 'Total ROI',    value: p(r.totalROI_yr1),     variant: 'rose' },
        ]))
      },
      { title: 'Statement', html:
        sectionHTML('Year 1 Operating Statement',
          '<div class="two-col">' +
          '<div>' +
          plRowsHTML([
            { label: 'Revenue', bold: true, value: '' },
            { label: `Rental Income — ${c(r.grossRentMonthly)}/mo`, value: c(r.grossRentYearly) },
            { label: `Vacancy Loss (${p(inp.vacancyRate)})`, value: `(${c(r.vacancyLossYearly)})`, red: true },
            { label: 'Net Rental Income', value: c(r.netRentalYearly) },
            { label: 'Other Income', value: c(inp.otherRevMonthly * 12) },
            { divider: true },
            { label: 'Gross Income', value: c(r.grossIncomeYearly), bold: true },
          ]) +
          '</div><div>' +
          plRowsHTML([
            { label: 'Expenses', bold: true, value: '' },
            { label: 'Property Taxes', value: `(${c(r.expTaxes)})`, red: true, indent: true },
            { label: 'Insurance', value: `(${c(r.expInsurance)})`, red: true, indent: true },
            { label: `Management ${p(inp.managementFeePct)}`, value: `(${c(r.expMgmt)})`, red: true, indent: true },
            { label: `Maintenance ${p(inp.maintenanceResPct)}`, value: `(${c(r.expMaint)})`, red: true, indent: true },
            ...(r.expUtilities > 0 ? [{ label: 'Utilities', value: `(${c(r.expUtilities)})`, red: true, indent: true }] : []),
            ...(r.expHoa > 0 ? [{ label: 'HOA', value: `(${c(r.expHoa)})`, red: true, indent: true }] : []),
            { divider: true },
            { label: 'Total Expenses', value: `(${c(r.totalExpenses)})`, bold: true, red: true },
          ]) +
          '</div></div>'
        ) +
        cardsHTML([
          { label: 'NOI', value: c(r.noi), sub: `${c(r.noi / 12)}/mo` },
          { label: 'Annual Mortgage', value: `(${c(r.annualMortgage)})`, sub: `${c(r.monthlyPayment)}/mo` },
          { label: 'Net Cash Flow', value: c(r.annualCashFlow), sub: `${c(r.monthlyCashFlow)}/mo`, color: r.annualCashFlow < 0 ? '#CA1551' : undefined },
        ])
      },
      { title: 'Pro Forma', html: sectionHTML('3-Year Pro Forma', (() => {
        const pfRows = r.proForma.map(yr => ({
          year: `Year ${yr.year}`,
          grossRent: c(yr.grossRent), vacancyLoss: c(-yr.vacancyLoss), grossIncome: c(yr.grossIncome),
          totalExpenses: c(-yr.totalExpenses), noi: c(yr.noi), cashFlow: c(yr.annualCashFlow),
          cashROI: p(yr.cashROI), totalROI: p(yr.totalROI),
        }))
        return tableHTML(
          [
            { label: '', key: 'year' },
            { label: 'Gross Rent', key: 'grossRent', align: 'right' },
            { label: 'Vacancy', key: 'vacancyLoss', align: 'right' },
            { label: 'Gross Income', key: 'grossIncome', align: 'right' },
            { label: 'Expenses', key: 'totalExpenses', align: 'right' },
            { label: 'NOI', key: 'noi', align: 'right' },
            { label: 'Cash Flow', key: 'cashFlow', align: 'right' },
            { label: 'Cash ROI', key: 'cashROI', align: 'right' },
            { label: 'Total ROI', key: 'totalROI', align: 'right' },
          ],
          pfRows
        )
      })()) },
      { title: 'Projections', html:
        sectionHTML('30-Year Return vs Time', longTermChartSVG(r.longTerm, { currency: c, pct: p })) +
        sectionHTML('30-Year Projections (Selected Years)', (() => {
          const years = [1, 2, 3, 5, 10, 15, 20, 25, 30]
          const projRows = years.filter(y => y <= r.longTerm.length).map(y => {
            const d = r.longTerm[y - 1]
            return {
              year: `Year ${y}`,
              grossIncome: c(d.grossIncome), expenses: c(d.totalExpenses),
              noi: c(d.noi), cashFlow: c(d.annualCashFlow),
              propValue: c(d.propertyValue), balance: c(d.remainingBalance),
              cocROI: p(d.cocROI), totalROI: p(d.totalROI),
            }
          })
          return tableHTML(
            [
              { label: '', key: 'year' },
              { label: 'Income', key: 'grossIncome', align: 'right' },
              { label: 'Expenses', key: 'expenses', align: 'right' },
              { label: 'NOI', key: 'noi', align: 'right' },
              { label: 'Cash Flow', key: 'cashFlow', align: 'right' },
              { label: 'Prop. Value', key: 'propValue', align: 'right' },
              { label: 'Balance', key: 'balance', align: 'right' },
              { label: 'CoC ROI', key: 'cocROI', align: 'right' },
              { label: 'Total ROI', key: 'totalROI', align: 'right' },
            ],
            projRows
          )
        })())
      },
      { title: 'CoC vs Down Payment', html:
        sectionHTML('CoC ROI vs Down Payment %', cocSensitivityChartSVG(r.cocSensitivity, inp.downPaymentPct, { pct: p })) +
        sectionHTML('Sensitivity Table', (() => {
          const sensRows = r.cocSensitivity.map(row => ({
            _highlight: Math.round(inp.downPaymentPct * 100) === row.pct,
            dp: `${row.pct}%${Math.round(inp.downPaymentPct * 100) === row.pct ? ' ◀' : ''}`,
            dpAmt: c(row.downPayment), mortgage: c(row.mortgage),
            cf: c(row.annualCashFlow), coc: p(row.cocROI),
          }))
          return tableHTML(
            [
              { label: 'Down %', key: 'dp' },
              { label: 'Down Amount', key: 'dpAmt', align: 'right' },
              { label: 'Mortgage/mo', key: 'mortgage', align: 'right' },
              { label: 'Annual CF', key: 'cf', align: 'right' },
              { label: 'COC ROI', key: 'coc', align: 'right' },
            ],
            sensRows
          )
        })())
      },
    ]
    openPrintView({ title: 'Rental ROI Calculator', subtitle: 'Analyze rental property investment returns', address, kpis, inputsHTML, pages })
  }, [r, inp, propTaxMode])

  return (
    <div className="calc">

      {/* ── Calculator Summary Header ──────────────────────────────────── */}
      <div className="calc-header">
        <div>
          <div className="calc-header__title">Rental ROI Calculator</div>
          <div className="calc-header__subtitle">Analyze rental property investment returns</div>
        </div>
        <div className="calc-header__kpis">
          {[
            { label: 'Annual Cash Flow', val: currency(r.annualCashFlow), color: r.annualCashFlow >= 0 ? '#5AC471' : 'var(--clr-rose)' },
            { label: 'Cap Rate',         val: pct(r.capRate),             color: r.capRate >= 0 ? '#5AC471' : 'var(--clr-rose)' },
            { label: 'CoC ROI',          val: pct(r.cocROI),              color: r.cocROI >= 0 ? '#5AC471' : 'var(--clr-rose)' },
            { label: 'Total ROI Yr1',    val: pct(r.totalROI_yr1),        color: r.totalROI_yr1 >= 0 ? '#5AC471' : 'var(--clr-rose)' },
            { label: 'Net Op. Income',   val: currency(r.noi),            color: r.noi >= 0 ? '#5AC471' : 'var(--clr-rose)' },
          ].map(({ label, val, color }, i, arr) => (
            <React.Fragment key={label}>
              <div className="calc-header__kpi">
                <div className="calc-header__kpi-label">{label}</div>
                <div className="calc-header__kpi-value" style={{ color }}>{val}</div>
              </div>
              {i < arr.length - 1 && <div className="calc-header__kpi-sep" />}
            </React.Fragment>
          ))}
        </div>
        <button className="calc-header__print" onClick={handlePrint} title="Print Report">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
          </svg>
        </button>
      </div>

      {/* ── Tab Bar ────────────────────────────────────────────────────── */}
      <div className="calc-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`calc-tabs__btn${tab === t ? ' calc-tabs__btn--active' : ''}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div className="calc-body">

        {/* ── LEFT: Inputs ──────────────────────────────────────────── */}
        <div className="calc-sidebar">

          <Group title="Property">
            <Field label="Purchase Price">
              <Inp value={inp.listingPrice} onChange={set('listingPrice')} pre="$" step="1000" />
            </Field>
            <div className="calc-row">
              <div className="calc-row__col">
                <Field label="Improvements">
                  <Inp value={inp.improvements} onChange={set('improvements')} pre="$" step="500" />
                </Field>
              </div>
              <div className="calc-row__col">
                <Field label="Year Built">
                  <Inp value={inp.yearBuilt} onChange={set('yearBuilt')} step="1" />
                </Field>
              </div>
            </div>
            <Field label="# of Units">
              <Inp value={inp.unitCount} onChange={set('unitCount')} step="1" min={1} />
            </Field>
          </Group>

          <Group title="Financing">
            <div className="calc-row calc-row--tight">
              <div className="calc-row__col"><Field label="Down %"><Inp value={inp.downPaymentPct * 100} onChange={setPct('downPaymentPct')} suf="%" step="1" /></Field></div>
              <div className="calc-row__col"><Field label="Rate %"><Inp value={inp.interestRate * 100} onChange={setPct('interestRate')} suf="%" step="0.125" /></Field></div>
              <div className="calc-row__col"><Field label="Years"><Inp value={inp.mortgageYears} onChange={set('mortgageYears')} step="1" /></Field></div>
            </div>
            <div className="calc-finance-box">
              {[
                { label: 'Loan', val: currency(r.loanAmount, 0) },
                { label: 'Payment', val: `${currency(r.monthlyPayment)}/mo` },
                { label: 'Outlay', val: currency(r.cashOutlay, 0) },
              ].map(({ label, val }, i, arr) => (
                <React.Fragment key={label}>
                  <div className="calc-finance-box__item">
                    <div className="calc-finance-box__label">{label}</div>
                    <div className="calc-finance-box__value">{val}</div>
                  </div>
                  {i < arr.length - 1 && <div className="calc-finance-box__sep" />}
                </React.Fragment>
              ))}
            </div>
          </Group>

          <Group title="Revenue">
            <div className="calc-row">
              <div className="calc-row__col"><Field label="Monthly Rent"><Inp value={inp.rentMonthly} onChange={set('rentMonthly')} pre="$" step="50" /></Field></div>
              <div className="calc-row__col"><Field label="Other Rev / Mo"><Inp value={inp.otherRevMonthly} onChange={set('otherRevMonthly')} pre="$" step="25" /></Field></div>
            </div>
            <Field label="Vacancy Rate" hint="% of rental income lost">
              <Inp value={inp.vacancyRate * 100} onChange={setPct('vacancyRate')} suf="%" step="0.5" />
            </Field>
          </Group>

          <Group title="Operating Expenses">
            <div className="calc-row">
              <div className="calc-row__col">
                <Field label="Property Tax/ YR">
                  {propTaxMode === 'pct'
                    ? <Inp value={inp.propertyTaxPct * 100} onChange={setPct('propertyTaxPct')} suf="%" step="0.1" />
                    : <Inp value={inp.propertyTaxFlat || 0} onChange={set('propertyTaxFlat')} pre="$" step="100" />
                  }
                  <div style={{ textAlign: 'right', marginTop: 2 }}>
                    <button className="calc-toggle-link" style={{ fontSize: 11 }} onClick={() => setPropTaxMode((m) => m === 'pct' ? 'flat' : 'pct')}>
                      Switch to {propTaxMode === 'pct' ? '$ Flat' : '% Rate'}
                    </button>
                  </div>
                </Field>
              </div>
              <div className="calc-row__col"><Field label="Insurance / yr"><Inp value={inp.insuranceYearly} onChange={set('insuranceYearly')} pre="$" step="100" /></Field></div>
            </div>
            <div className="calc-row">
              <div className="calc-row__col"><Field label="Management %" hint="% of gross rent"><Inp value={inp.managementFeePct * 100} onChange={setPct('managementFeePct')} suf="%" step="0.5" /></Field></div>
              <div className="calc-row__col"><Field label="Maintenance %" hint="% of gross rent"><Inp value={inp.maintenanceResPct * 100} onChange={setPct('maintenanceResPct')} suf="%" step="0.5" /></Field></div>
            </div>
            <div className="calc-row">
              <div className="calc-row__col"><Field label="Utilities / mo"><Inp value={inp.utilitiesMonthly} onChange={set('utilitiesMonthly')} pre="$" step="10" /></Field></div>
              <div className="calc-row__col"><Field label="HOA / yr"><Inp value={inp.hoaYearly} onChange={set('hoaYearly')} pre="$" step="100" /></Field></div>
            </div>
            <div className="calc-row">
              <div className="calc-row__col"><Field label="Advertising / yr"><Inp value={inp.advertisingYearly} onChange={set('advertisingYearly')} pre="$" step="50" /></Field></div>
              <div className="calc-row__col"><Field label="Misc / yr"><Inp value={inp.miscYearly} onChange={set('miscYearly')} pre="$" step="50" /></Field></div>
            </div>
          </Group>

          <Group title="Growth Assumptions">
            <div className="calc-row calc-row--tight">
              <div className="calc-row__col"><Field label="Revenue" hint="Annual %"><Inp value={inp.revenueGrowthPct * 100} onChange={setPct('revenueGrowthPct')} suf="%" step="0.25" /></Field></div>
              <div className="calc-row__col"><Field label="Expenses" hint="Annual %"><Inp value={inp.expenseGrowthPct * 100} onChange={setPct('expenseGrowthPct')} suf="%" step="0.25" /></Field></div>
              <div className="calc-row__col"><Field label="Apprec." hint="Annual %"><Inp value={inp.appreciationPct * 100} onChange={setPct('appreciationPct')} suf="%" step="0.25" /></Field></div>
            </div>
          </Group>
        </div>

        {/* ── RIGHT: Tabbed Dashboard ──────────────────────────────────── */}
        <div className="calc-dashboard">

          {/* OVERVIEW */}
          {tab === 'Overview' && (
            <>
              <SectionBanner title="Key Performance Indicators" />
              <div className="calc-row">
                <StatCard label="Annual Cash Flow"  value={currency(r.annualCashFlow)}  sub={`${currency(r.monthlyCashFlow)}/mo`} rose={r.annualCashFlow < 0} />
                <StatCard label="Cap Rate"           value={pct(r.capRate)}         sub="NOI ÷ price" />
                <StatCard label="Cash-on-Cash ROI"   value={pct(r.cocROI)}          sub="Year 1" rose={r.cocROI < 0} />
                <StatCard label="Total ROI Year 1"   value={pct(r.totalROI_yr1)}    sub="Cash+Equity+Apprec." rose={r.totalROI_yr1 < 0} />
              </div>
              <div className="calc-row">
                <StatCard label="NOI"                value={currency(r.noi)}             sub="Net operating income" />
                <StatCard label="GRM"                value={num(r.grm, 1)}         sub="Price ÷ annual rent" />
                <StatCard label="Cost / Unit"        value={currency(r.costPerUnit, 0)}  sub={`${inp.unitCount} unit${inp.unitCount !== 1 ? 's' : ''}`} />
                <StatCard label="Monthly CF / Unit"  value={currency(r.monthlyPerUnit)}  sub="Per unit / month" />
              </div>

              <SectionBanner title="Acquisition Cost Stack" />
              <div className="calc-row">
                {[
                  { label: 'Sales Price',    v: r.salesPrice },
                  { label: '+ Closing 2%',  v: r.closingCosts },
                  { label: '+ Improvements',v: r.improvements },
                  { label: '= Total Cost',   v: r.totalCost,  variant: 'teal' },
                  { label: 'Down Payment',   v: r.downPayment, variant: 'orange' },
                  { label: 'Cash Outlay',  v: r.cashOutlay,  variant: 'orange' },
                ].map(({ label, v, variant }) => (
                  <div key={label} className={`calc-pill${variant ? ` calc-pill--${variant}` : ''}`}>
                    <div className="calc-pill__label">{label}</div>
                    <div className="calc-pill__value">{currency(v, 0)}</div>
                  </div>
                ))}
              </div>

              <SectionBanner title="Year 1 Return Breakdown" />
              <div className="calc-row">
                {[
                  { label: 'Cash Flow',    num: r.annualCashFlow,   fmt: currency },
                  { label: 'Equity',       num: r.equity_yr1,       fmt: currency },
                  { label: 'Appreciation', num: r.appreciation_yr1, fmt: currency },
                  { label: 'Total Return', num: r.totalReturn_yr1,  fmt: currency, bold: true },
                  { label: 'Total ROI',    num: r.totalROI_yr1,     fmt: pct,      bold: true },
                ].map(({ label, num: val, fmt, bold }) => (
                  <div key={label} className={`calc-pill${bold ? ' calc-pill--rose' : ''}`} style={{ padding: 6.25 }}>
                    <div className="calc-pill__label">{label}</div>
                    <div className="calc-pill__value" style={!bold && val < 0 ? { color: 'var(--clr-rose)' } : undefined}>{fmt(val)}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* STATEMENT */}
          {tab === 'Statement' && (
            <>
              <SectionBanner title="Year 1 Operating Statement" />
              <div className="calc-card">
                <div className="calc-statement-grid">
                  <div>
                    <p className="calc-statement__heading">Revenue</p>
                    <PLRow label={`Rental Income — ${currency(r.grossRentMonthly)}/mo`} value={currency(r.grossRentYearly)} />
                    <PLRow label={`Vacancy Loss (${pct(inp.vacancyRate, 2)})`} value={negCurrency(r.vacancyLossYearly)} />
                    <PLRow label="Net Rental Income" value={currency(r.netRentalYearly)} />
                    <PLRow label="Other Income" value={currency(inp.otherRevMonthly * 12)} />
                    <div className="calc-statement__divider">
                      <PLRow label="Gross Income" value={currency(r.grossIncomeYearly)} bold green />
                    </div>
                  </div>
                  <div>
                    <p className="calc-statement__heading">Expenses</p>
                    <PLRow label="Property Taxes" value={negCurrency(r.expTaxes)} />
                    <PLRow label="Insurance" value={negCurrency(r.expInsurance)} />
                    <PLRow label={`Management ${pct(inp.managementFeePct, 2)} of rent`} value={negCurrency(r.expMgmt)} />
                    <PLRow label={`Maintenance ${pct(inp.maintenanceResPct, 2)} of rent`} value={negCurrency(r.expMaint)} />
                    {r.expUtilities > 0 && <PLRow label="Utilities" value={negCurrency(r.expUtilities)} />}
                    {r.expHoa > 0 && <PLRow label="HOA" value={negCurrency(r.expHoa)} />}
                    {r.expAdvertising > 0 && <PLRow label="Advertising" value={negCurrency(r.expAdvertising)} />}
                    {r.expMisc > 0 && <PLRow label="Misc" value={negCurrency(r.expMisc)} />}
                    <div className="calc-statement__divider">
                      <PLRow label="Total Expenses" value={negCurrency(r.totalExpenses)} bold red />
                    </div>
                  </div>
                </div>
              </div>
              <div className="calc-row">
                <StatCard label="NOI"              value={currency(r.noi)}             sub={`${currency(r.noi / 12)}/mo`} />
                <StatCard label="Annual Mortgage"  value={`(${currency(r.annualMortgage)})`} sub={`${currency(r.monthlyPayment)}/mo`} />
                <StatCard label="Net Cash Flow"    value={currency(r.annualCashFlow)}  sub={`${currency(r.monthlyCashFlow)}/mo`} rose={r.annualCashFlow < 0} />
              </div>
            </>
          )}

          {/* PRO FORMA */}
          {tab === 'Pro Forma' && (
            <>
              <SectionBanner title="3-Year Pro Forma" />
              <div className="calc-card">
                <ProFormaTable proForma={r.proForma} />
              </div>
            </>
          )}

          {/* PROJECTIONS */}
          {tab === 'Projections' && (
            <>
              <SectionBanner title="30-Year Return vs Time" />
              <div className="calc-card">
                <LongTermChart longTerm={r.longTerm} />
              </div>
              <div className="calc-row">
                {[
                  { label: 'Year 1 Total ROI',  v: pct(r.longTerm[0]?.totalROI),  raw: r.longTerm[0]?.totalROI },
                  { label: 'Year 5 Total ROI',  v: pct(r.longTerm[4]?.totalROI),  raw: r.longTerm[4]?.totalROI },
                  { label: 'Year 10 Total ROI', v: pct(r.longTerm[9]?.totalROI),  raw: r.longTerm[9]?.totalROI },
                  { label: 'Year 30 Total ROI', v: pct(r.longTerm[29]?.totalROI), raw: r.longTerm[29]?.totalROI },
                ].map(({ label, v, raw }) => (
                  <StatCard key={label} label={label} value={v} rose={raw < 0} />
                ))}
              </div>
            </>
          )}

          {/* SENSITIVITY */}
          {tab === 'CoC vs Down Payment' && (
            <>
              <SectionBanner title="Cash-on-Cash ROI vs Down Payment %" />
              <div className="calc-card">
                <CocSensitivityChart data={r.cocSensitivity} currentPct={inp.downPaymentPct} />
              </div>
              <div className="calc-card calc-card--no-pad">
                <table className="calc-sens-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Down %</th>
                      <th style={{ textAlign: 'right' }}>Down Amt</th>
                      <th style={{ textAlign: 'right' }}>Mortgage/mo</th>
                      <th style={{ textAlign: 'right' }}>Annual CF</th>
                      <th style={{ textAlign: 'right' }}>COC ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.cocSensitivity.map((row) => {
                      const isCurrent = Math.round(inp.downPaymentPct * 100) === row.pct
                      return (
                        <tr key={row.pct} className={isCurrent ? 'calc-sens-table__current' : undefined}>
                          <td style={{ fontWeight: isCurrent ? 700 : 400, color: isCurrent ? 'var(--clr-orange)' : undefined }}>{row.pct}%{isCurrent ? ' ◀' : ''}</td>
                          <td style={{ textAlign: 'right' }}>{currency(row.downPayment, 0)}</td>
                          <td style={{ textAlign: 'right' }}>{currency(row.mortgage)}</td>
                          <td style={{ textAlign: 'right', color: row.annualCashFlow >= 0 ? 'var(--clr-green)' : 'var(--clr-rose)' }}>{currency(row.annualCashFlow)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: row.cocROI >= 0 ? 'var(--clr-teal)' : 'var(--clr-rose)' }}>{pct(row.cocROI)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
})

export default RentalROICalculator
